/*
 * The prompt page — the app's front door, and the only place a video starts.
 *
 * Figma draws it as `Prompt Page - default`, with four states documented in
 * `Prompt Box Interactions`: empty, add-menu open, shelf submenu open, and text
 * entered ready to generate. One card, 769x260, carrying a title line, the body,
 * and a footer holding the `+` menu on the left and the send control on the right.
 *
 * Three things the frames do not draw, kept anyway because dropping them would
 * change what the engine renders or what it charges:
 *
 *   - Style (storybook / photoreal) and a Non-narrative checkbox sit in the footer.
 *     They are two questions — how it looks and who speaks — and all four
 *     combinations are valid, so narration cannot be a third style segment without
 *     making the user trade one answer for the other. Style is always sent;
 *     the mode only when it is turned on, which is not the engine's default.
 *   - model / duration / max scenes / aspect ratio / image guidance sit behind the
 *     `Advanced` disclosure, VITE_DEBUG-gated exactly as on the page this replaces.
 *     They are passed through untouched: `videoGenerationApi` resolves them with
 *     `resolveModelName` / `resolveDurationSeconds`, and those decide the size of
 *     the credit hold. Defaulting them a second time here is how a quote and a
 *     hold drift apart.
 *   - The wallet nudges, the upload limits and the error/retry paths, none of
 *     which the frames show but all of which the flow needs.
 *
 * One deliberate departure. The empty state draws a microphone where the send
 * control sits, swapping to a green arrow once there is text. There is no
 * dictation behind it, and this codebase has already decided once that a control
 * which cannot do the thing it depicts is worse than no control (see the absent
 * `Rename project` in the sidebar's job menu). So the slot holds the send button
 * in both states — disabled while empty, green when ready. It is one affordance
 * that always means the same thing, and the mic returns when something can
 * answer it.
 */
import { type DragEvent, type FormEvent, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { logger } from '@/shared/utils/logger';
import { useProcessing } from '@/app/providers/processing';
import { useWallet } from '@/app/providers/wallet';
import { useShelves } from '@/features/library/hooks/useShelves';
import { getMaxFileSizeBytes } from '@/features/generation/services/submissionService';
import { balanceLevel } from '@/features/wallet/utils/walletBalance';
import { formatCredits } from '@/shared/utils/format';
import type { SubmissionPayload } from '@/shared/types';
import { isAcceptedFile, ACCEPTED_EXTENSIONS } from '@/features/generation/services/fileExtraction';
import { isAbortError } from '@/shared/api/apiClient';
import { ErrorBanner } from '@/shared/ui/ErrorBanner';
import { describeJobError } from '@/shared/api/jobErrors';
import { ConfirmGenerationModal } from '@/features/generation/components/ConfirmGenerationModal';
import { PaperCandidates } from '@/features/generation/components/PaperCandidates';
import { PaperPending } from '@/features/generation/components/PaperPending';
import {
  looksLikePdfUrl,
  pdfUrlLabel,
  searchPapers,
  type PaperHit,
} from '@/features/generation/services/papersApi';
import {
  ArrowUpIcon,
  Button,
  Checkbox,
  CloseIcon,
  IconButton,
  InfoIcon,
  ImageIcon,
  Menu,
  MenuItem,
  MenuSeparator,
  MenuSubmenu,
  PlusIcon,
  Segmented,
  ShelfIcon,
  Tooltip,
  cx,
} from '@/shared/ui';

const readableFileSize = (bytes: number) => `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

export default function PromptPage() {
  const navigate = useNavigate();
  const { submitJob, jobs, confirmJob, cancelJob } = useProcessing();
  const { shelves, addTo, create } = useShelves();
  const { balance } = useWallet();

  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  // The raw document. POST /videos parses it server-side, so there is nothing to
  // extract or preview here — exactly one of `file` or `text` is ever sent.
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [style, setStyle] = useState<'storybook' | 'photoreal'>('storybook');
  /* How the words are delivered, which is a different question from how the video
     looks — so it is its own control rather than a third option in the style picker.
     A checkbox rather than two segments: narrated is the ordinary behaviour and this
     is the departure from it, which is not a choice between peers. Off by default,
     which is what every job did before this existed. */
  const [nonNarrative, setNonNarrative] = useState(false);
  /* Opt-in pause to look at the characters before any scene is rendered. Off by
     default: most videos should not stop and wait for the user. */
  /* Research mode is an explicit choice, not something sniffed out of the
     prompt text. The page this replaces detected a pasted arXiv link, which
     meant a prompt that merely mentioned a paper became a paper video. */
  const [researchMode, setResearchMode] = useState(false);
  const [candidates, setCandidates] = useState<PaperHit[] | null>(null);
  const [candidateSource, setCandidateSource] = useState('arxiv');
  const [searchedFor, setSearchedFor] = useState('');
  /* The arXiv id currently being turned into a video, so the list can show
     which row is working and refuse a second pick. */
  /* Non-null while a paper submission is in flight. Holds what is being read
     so the pending panel can name it; the request is synchronous and ~100s,
     and showing nothing for that long reads as a hang. */
  const [readingSubject, setReadingSubject] = useState<string | null>(null);
  /* Aborts the in-flight paper request. A ref rather than state: nothing renders
     from it, and it must be readable by the handler that created it without waiting
     for a re-render. Held here rather than inside PaperPending because the request
     outlives any one panel — the panel is a view of it, not its owner. */
  const readingAbort = useRef<AbortController | null>(null);
  const [pendingJobId, setPendingJobId] = useState<string | null>(null);

  /* `Add to shelf` is offered before the video exists, so there is no id to file
     it under yet. The choice is held here and applied once submitJob returns one
     — the same id the sidebar's job card files membership under, so a shelf
     picked here and a shelf picked there land on the same row. */
  const [stagedShelfIds, setStagedShelfIds] = useState<string[]>([]);
  const [creatingShelf, setCreatingShelf] = useState(false);
  const [newShelfName, setNewShelfName] = useState('');

  const [error, setError] = useState<string | null>(null);
  const [showWalletCta, setShowWalletCta] = useState(false);
  // Whether the user has pressed send while it was blocked. The copy it raises
  // is derived from the block itself (see `banner`), not stored here.
  const [blockedNotice, setBlockedNotice] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  /* Where PaperPending hands focus back when the control it took focus from is
     gone -- which is the candidate path, whose pressed row unmounts with the
     list. The textarea is the one control mounted on all three paper paths for
     the whole wait, and it is where a user acts next after a failed read. */
  const textRef = useRef<HTMLTextAreaElement>(null);
  const submittingRef = useRef(false);

  const [debugUserId, setDebugUserId] = useState('');
  const [debugSessionId, setDebugSessionId] = useState('');
  const [debugModelName, setDebugModelName] = useState('');
  const [debugDuration, setDebugDuration] = useState<number | ''>('');
  const [debugMaxScenes, setDebugMaxScenes] = useState<number | ''>('');
  const [debugAspectRatio, setDebugAspectRatio] = useState('');
  const [debugImageGuidance, setDebugImageGuidance] = useState(false);

  const resetDebugDefaults = () => {
    setDebugUserId('');
    setDebugSessionId('');
    setDebugModelName('');
    setDebugDuration('');
    setDebugMaxScenes('');
    setDebugAspectRatio('');
    setDebugImageGuidance(false);
  };

  const maxFileSize = getMaxFileSizeBytes();

  const clearFile = () => {
    // Drop any upload error with the file, or an "unsupported type" banner
    // lingers over a card that no longer has the offending file in it.
    setError(null);
    setShowWalletCta(false);
    setBlockedNotice(false);
    setSelectedFile(null);
    // The input keeps its last selection; reset it so the same file can be
    // re-picked after removing, and so a stale value cannot be re-submitted.
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFile = (file: File) => {
    setError(null);
    setShowWalletCta(false);
    setBlockedNotice(false);
    // `accept` only filters the picker, never a drop, so re-check the type here.
    if (!isAcceptedFile(file)) {
      setError(`Unsupported file type. Use ${ACCEPTED_EXTENSIONS.join(', ')}.`);
      return;
    }
    if (file.size > maxFileSize) {
      setError(`File size exceeds ${readableFileSize(maxFileSize)}.`);
      return;
    }
    setSelectedFile(file);
  };

  const handleDragOver = (event: DragEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!isDragging) setIsDragging(true);
  };

  const handleDragLeave = (event: DragEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (event: DragEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const toggleStagedShelf = (shelfId: string) => {
    setStagedShelfIds((prev) =>
      prev.includes(shelfId) ? prev.filter((id) => id !== shelfId) : [...prev, shelfId],
    );
  };

  const commitNewShelf = async () => {
    const name = newShelfName.trim();
    setNewShelfName('');
    setCreatingShelf(false);
    if (!name) return;
    await create(name);
    /* Deliberately not staged. `create` resolves with the collection, not the new
       shelf, so there is no id to stage without guessing at one by name — and two
       shelves may share a name. The new shelf appears in the menu; picking it is
       one more click and cannot attach the wrong row. */
  };

  const balanceCredits = balance?.balanceCredits ?? null;
  const level = balanceLevel(balanceCredits);
  // Zero credits cannot afford any video. Per-submission affordability is the
  // server's call at submit time (400 insufficient_credits), surfaced by the
  // error banner and its wallet link below.
  const blockedForFunds = level === 'empty';

  // A file wins when present: the payload carries exactly one source, and the
  // textarea is disabled while one is attached so the card cannot imply both.
  const usingFile = selectedFile !== null;
  const hasContent = usingFile || text.trim().length > 0;

  const wordCount = text.trim() ? text.trim().split(/\s+/).length : 0;

  /* What the picker will actually take, stated before you pick rather than
     after you pick wrong. The page this replaces put it in the dropzone; there
     is no dropzone any more — the whole card is the drop target — so it goes on
     the row that opens the picker. Derived from the two sources of truth so a
     new extension or a raised cap cannot leave this line behind. */
  const uploadHint = `${ACCEPTED_EXTENSIONS.join(', ')} · max ${readableFileSize(maxFileSize)}`;

  /* Why the send control will not fire, in the words the old submit button
     carried as its label ("Add text or a file to submit" / "Add funds to
     submit"). The frames replace that label with an arrow, so the reason has to
     live somewhere else: it is the button's accessible name, its tooltip, and
     what pressing it puts in the error banner.

     Hence `aria-disabled` below rather than `disabled`. A disabled button is
     neither focusable nor hoverable nor clickable, which shuts all three of
     those routes at once — the control just stops responding and never says
     why. Every path out of here is guarded in `handleSubmit`. */
  const blocked = blockedForFunds
    ? {
        hint: 'Add funds to generate',
        message: 'You have no credits. Add funds to generate a video.',
        walletCta: true,
      }
    : !hasContent
      ? researchMode
        ? {
            hint: 'Paste a link, name a paper, or attach a PDF',
            message:
              'Paste an arXiv link, type the name of a research paper, or attach '
              + 'its PDF.',
            walletCta: false,
          }
        : {
            hint: 'Add text or a document to generate',
            message: 'Paste some text or attach a document to begin.',
            walletCta: false,
          }
      : null;
  const canSubmit = blocked === null && !isSubmitting;

  /* The banner, from whichever source has something to say. The blocked notice
     is derived rather than stored, so it cannot outlive the condition it
     describes: put it in `error` and pressing send on an empty box leaves
     "Paste some text or attach a document to begin." sitting over a textarea
     the user has since filled. A submission failure is a fact about a request
     that really happened, so that one is state. */
  const banner =
    blockedNotice && blocked
      ? { message: blocked.message, walletCta: blocked.walletCta }
      : error
        ? { message: error, walletCta: showWalletCta }
        : null;

  const dismissBanner = () => {
    setError(null);
    setShowWalletCta(false);
    setBlockedNotice(false);
  };

  /* Picking a candidate is the expensive step: the backend downloads the PDF,
     reads it, plans the beats and renders their assets before answering, which
     takes up to a minute. It lands in `pendingJobId` exactly like a normal
     submission, so estimate, confirm, progress and playback are unchanged. */
  /* Clears the panel and drops the controller. Called from every exit -- success,
     failure and cancel -- so a stale controller can never abort the NEXT request. */
  const finishReading = () => {
    readingAbort.current = null;
    setReadingSubject(null);
  };

  const cancelReading = () => {
    readingAbort.current?.abort();
    finishReading();
  };

  const handlePickPaper = async (hit: PaperHit) => {
    /* Re-entrancy, guarded on the state that is actually rendered rather than on a
       second flag kept in lockstep with it. The next step is a 30-60s request and two
       in flight would place two holds for one intent. */
    if (readingSubject !== null) return;
    setError(null);
    setShowWalletCta(false);
    try {
      setReadingSubject(hit.title);
      readingAbort.current = new AbortController();
      /* Through submitJob, NOT createPaperVideo directly. submitJob is what
         registers the job with the processing provider, and `pendingJob` is
         looked up from that list — calling the papers API straight from here
         created the job server-side but left the confirm modal waiting on a job
         it could never find, so the page span forever on a video that existed. */
      const jobId = await submitJob({
        arxivId: hit.arxivId,
        signal: readingAbort.current.signal,
      });
      setPendingJobId(jobId);
      setCandidates(null);
      setSearchedFor('');
      setTitle('');
      setText('');
      setStagedShelfIds([]);
      clearFile();
    } catch (paperError) {
      /* Cancel is not an error. Saying "network error" to someone who just pressed
         the button they were given is worse than saying nothing, and their typed
         title is deliberately still in the box so they can pick again. */
      if (!isAbortError(paperError)) {
        const info = describeJobError(paperError);
        setError(info.message);
        setShowWalletCta(info.walletCta);
      }
    } finally {
      finishReading();
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setShowWalletCta(false);
    setBlockedNotice(false);

    // The button is `aria-disabled`, not `disabled`, so this is reachable —
    // pressing it is the third route to the reason, after the accessible name
    // and the tooltip.
    if (submittingRef.current || isSubmitting) return;
    if (blocked) {
      setBlockedNotice(true);
      return;
    }

    try {
      submittingRef.current = true;
      setIsSubmitting(true);

      /* An attached PDF IS the paper, so there is nothing to search for and no
         candidate to choose between — it submits directly. The server reads the
         bytes the same way it reads a downloaded one; what it cannot know is the
         licence, so figure excerpts are refused and everything redrawn from the
         numbers still works. */
      if (researchMode && usingFile) {
        setReadingSubject(selectedFile.name);
        readingAbort.current = new AbortController();
        const jobId = await submitJob({
          paperFile: selectedFile,
          signal: readingAbort.current.signal,
        });
        setPendingJobId(jobId);
        setTitle('');
        setText('');
        setStagedShelfIds([]);
        clearFile();
        return;
      }

      /* A pasted PDF link is the paper too, so it also submits directly. An
         arXiv link is NOT handled here: it falls through to the search call,
         where the engine's own identifier check resolves it and settles the
         licence — a second arXiv matcher in the client would drift. */
      if (researchMode && looksLikePdfUrl(text)) {
        /* Name it, like the other two paths do -- the upload path shows
           `selectedFile.name` and the candidate path shows the paper's title.
           This was the one case with a user-supplied identifier and the only
           one that did not show it back, so a mistyped URL read as a generic
           wait rather than as the wrong paper. */
        setReadingSubject(pdfUrlLabel(text));
        readingAbort.current = new AbortController();
        const jobId = await submitJob({
          paperUrl: text.trim(),
          signal: readingAbort.current.signal,
        });
        setPendingJobId(jobId);
        setTitle('');
        setText('');
        setStagedShelfIds([]);
        clearFile();
        return;
      }

      /* Research mode's send button otherwise searches; it does not submit. The
         expensive call happens when a candidate is picked, so nothing is spent on
         a title that matched the wrong paper. */
      if (researchMode) {
        const query = text.trim();
        const found = await searchPapers(query);
        setCandidates(found.results);
        setCandidateSource(found.source);
        setSearchedFor(query);
        return;
      }

      const payload: SubmissionPayload = {
        // Left off when blank so the backend generates one from the text; only a
        // title the user actually typed is sent.
        title: title.trim() || undefined,
        ...(usingFile ? { file: selectedFile, fileName: selectedFile.name } : { text }),
        style,
        // The engine's default mode is storytelling, which is what "Narrated" means, so
        // only the opted-in value needs sending. Spelling both out would also work; this
        // keeps a narrated payload byte-identical to what it was before this control.
        ...(nonNarrative ? { mode: 'non-narrative' as const } : {}),
      };

      if (debugUserId) payload.userId = debugUserId;
      if (debugSessionId) payload.sessionId = debugSessionId;
      if (debugModelName) payload.modelName = debugModelName;
      if (debugDuration !== '') payload.duration = Number(debugDuration);
      if (debugMaxScenes !== '') payload.maxScenes = Number(debugMaxScenes);
      if (debugAspectRatio) payload.aspectRatio = debugAspectRatio;
      if (debugImageGuidance) payload.imageGuidance = true;

      /* Shape and knobs only, never the payload. `payload.text` is the user's
         entire pasted document and the object also carries userId/sessionId, so
         logging it verbatim dumps the whole submission — content and identity —
         into the console. */
      logger.log('Submitting job:', {
        titleLength: payload.title?.length ?? 0,
        textLength: payload.text?.length ?? 0,
        fileName: payload.fileName,
        modelName: payload.modelName,
        duration: payload.duration,
        maxScenes: payload.maxScenes,
        aspectRatio: payload.aspectRatio,
        imageGuidance: payload.imageGuidance,
      });

      const jobId = await submitJob(payload);

      /* Shelves are filed after the job exists, and a failure here must not read
         as a failed submission — the video is already generating. */
      for (const shelfId of stagedShelfIds) {
        try {
          await addTo(jobId, shelfId);
        } catch (shelfError) {
          logger.error('Failed to file the new job on a shelf', shelfError);
        }
      }

      setPendingJobId(jobId);
      setTitle('');
      setText('');
      setStagedShelfIds([]);
      clearFile();
    } catch (submissionError) {
      if (!isAbortError(submissionError)) {
        const info = describeJobError(submissionError);
        setError(info.message);
        setShowWalletCta(info.walletCta);
      }
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
      finishReading();
    }
  };

  const pendingJob = pendingJobId ? jobs.find((job) => job.id === pendingJobId) : undefined;
  const stagedShelves = shelves.filter((shelf) => stagedShelfIds.includes(shelf.id));

  /* Built-ins are excluded, matching the sidebar's job menu and the library's
     card menu: Saved and Favorites are a toggle on an item, not somewhere to
     file it. The frame agrees — it lists only the user's own shelves. */
  const movableShelves = shelves.filter((shelf) => !shelf.builtin);

  return (
    <div className="prompt-page">
      <h1 className="prompt-page__headline">What are we bringing to life today?</h1>

      <form
        className={cx('prompt-box', isDragging && 'is-dragging')}
        onSubmit={handleSubmit}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          type="text"
          className="prompt-box__title"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          placeholder="Give this project a title (optional)"
          aria-label="Project title (optional)"
        />

        <textarea
          ref={textRef}
          className="prompt-box__text"
          value={text}
          onChange={(event) => {
            setText(event.target.value);
            // Answering the notice retires it, so emptying the box again does
            // not bring back a complaint the user was never re-shown.
            setBlockedNotice(false);
          }}
          placeholder={
            researchMode
              ? usingFile
                ? 'Your PDF will be read as the paper.'
                : 'Paste an arXiv or PDF link, name a paper, or attach its PDF'
              : usingFile
                ? 'Your document will be used as the source.'
                : 'Paste text or upload a document to begin.'
          }
          aria-label="Source text"
          disabled={usingFile}
        />

        {/* Only once there is something to count. "0 characters · 0 words" under
            an empty composer is a label for a fact the empty box already states,
            on the one screen whose whole job is the headline and the box. */}
        {!usingFile && text.length > 0 ? (
          <p className="prompt-box__count">
            {text.length} characters · {wordCount} words
          </p>
        ) : null}

        {selectedFile ? (
          <p className="prompt-box__file">
            <span className="prompt-box__file-name">{selectedFile.name}</span>
            <span className="prompt-box__file-size">{readableFileSize(selectedFile.size)}</span>
            {/* Replace, not just Remove. Swapping one document for another was
                remove-then-reopen-the-menu, which is two steps and a disappearing
                target for what is one intention. */}
            <Button size="sm" variant="ghost" onClick={() => fileInputRef.current?.click()}>
              Replace
            </Button>
            <IconButton label={`Remove ${selectedFile.name}`} size="sm" variant="ghost" onClick={clearFile}>
              <CloseIcon size={14} />
            </IconButton>
          </p>
        ) : null}

        {stagedShelves.length > 0 ? (
          <ul className="prompt-box__shelves" aria-label="Shelves this will be added to">
            {stagedShelves.map((shelf) => (
              <li key={shelf.id}>
                <button
                  type="button"
                  className="prompt-box__shelf-chip"
                  onClick={() => toggleStagedShelf(shelf.id)}
                  aria-label={`Don't add to ${shelf.name}`}
                >
                  {shelf.name}
                  <CloseIcon size={12} />
                </button>
              </li>
            ))}
          </ul>
        ) : null}

        <div className="prompt-box__foot">
          {/* Everything except send. The send button must not be part of the wrap:
              as a plain sibling it is the LAST flex item, so any row too wide for the
              card pushes send onto a second line rather than a picker or the wordmark,
              and it lands bottom-left because the auto margin that pins it right lives
              on the wordmark, which stayed on line one. Grouping the controls lets them
              wrap among themselves while send stays put. */}
          <div className="prompt-box__controls">
            <Menu
              label="Add to this project"
              // Hangs below the card, as the frame draws it — there is nothing
              // under the prompt box for it to collide with.
              placement="bottom-start"
              /* Wider than the 180px default, because one of its rows carries the
                 upload constraint. An absolutely positioned menu shrink-to-fits
                 against its anchor — a 32px icon button — so without a width the
                 row's label wraps to one word per line. */
              className="prompt-add-menu"
              trigger={
                <IconButton label="Add to this project" size="sm" variant="ghost">
                  <PlusIcon size={18} />
                </IconButton>
              }
            >
              {/* The constraint under the action, not beside it. `MenuItem`'s
                  `detail` slot is sized for a shortcut or a tick; a line this long
                  sitting in it squeezes the label to one word per row. */}
              <MenuItem icon={<ImageIcon size={16} />} onSelect={() => fileInputRef.current?.click()}>
                <span className="prompt-add-menu__label">Add file or photo</span>
                <span className="prompt-add-menu__hint">{uploadHint}</span>
              </MenuItem>

              {movableShelves.length > 0 ? (
                <MenuSubmenu label="Add to shelf" icon={<ShelfIcon size={16} />}>
                  {movableShelves.map((shelf) => (
                    <MenuItem
                      key={shelf.id}
                      onSelect={() => toggleStagedShelf(shelf.id)}
                      checked={stagedShelfIds.includes(shelf.id)}
                    >
                      {shelf.name}
                    </MenuItem>
                  ))}
                  <MenuSeparator />
                  <MenuItem icon={<PlusIcon size={16} />} onSelect={() => setCreatingShelf(true)}>
                    Start a new shelf
                  </MenuItem>
                </MenuSubmenu>
              ) : (
                <MenuItem icon={<PlusIcon size={16} />} onSelect={() => setCreatingShelf(true)}>
                  Start a new shelf
                </MenuItem>
              )}
            </Menu>

            <Segmented
              label="Source"
              value={researchMode ? 'paper' : 'text'}
              onChange={(next) => {
                setResearchMode(next === 'paper');
                /* Candidates belong to the query that produced them; leaving them
                   up after a mode switch offers papers for text the box no longer
                   holds. */
                setCandidates(null);
                setSearchedFor('');
                setBlockedNotice(false);
              }}
              options={[
                { value: 'text', label: 'Text' },
                { value: 'paper', label: 'Research paper' },
              ]}
            />

            {/* Paper videos are documentation-styled server-side, so offering
                Storybook/Photoreal here would be a control that does nothing. A
                badge states the style instead of pretending to choose it. */}
            {researchMode ? (
              <span className="prompt-box__style-badge">Paper explainer</span>
            ) : (
              <Segmented
                label="Visual style"
                value={style}
                onChange={setStyle}
                options={[
                  { value: 'storybook', label: 'Storybook' },
                  { value: 'photoreal', label: 'Photoreal' },
                ]}
              />
            )}

            {/* Delivery, not look. A video can be storybook-narrated, storybook-acted,
                photoreal-narrated or photoreal-acted, so this cannot live in the style
                control without making the user trade one choice for the other. Hidden for
                paper videos for the same reason the style picker is: the backend narrates
                those, and a control that does nothing is worse than no control.

                The info affordance is not decoration. "Non-narrative" names the option
                without explaining it, and the difference it makes — who is speaking, and
                in whose voice — is not guessable from two words on a crowded footer. */}
            {!researchMode && (
              <Checkbox
                label="Non-narrative"
                checked={nonNarrative}
                onChange={setNonNarrative}
                adornment={
                  <Tooltip
                    placement="top"
                    content="Characters speak their own lines, each in their own voice, instead of a narrator reading your text. Works with either visual style."
                  >
                    {/* A real IconButton, not a span wearing role="button". The span was
                        focusable and announced as a button while Enter and Space did
                        nothing — the page's own rule about controls that cannot do what
                        they depict. `Button` defaults to type="button", so it is safe
                        inside the form. NOT `decorative`: that flag is for a tip that
                        merely restates its trigger's name, and this one carries the only
                        explanation of the feature anywhere on the page — it has to reach a
                        screen reader through aria-describedby, not be hidden from it. */}
                    <IconButton
                      label="About non-narrative videos"
                      size="sm"
                      variant="ghost"
                      className="checkbox__info"
                    >
                      <InfoIcon size={16} />
                    </IconButton>
                  </Tooltip>
                }
              />
            )}

            <span className="prompt-box__brand">Powered by Rachel 1.0</span>
          </div>

          {/* `disabled` on the Tooltip, not on the button: the label is only
              worth showing when there is a reason, and toggling the wrapper in
              and out of the tree instead would remount the control mid-click.

              The class names the send button's slot in the footer row rather
              than the tooltip. Below 640px the wordmark that used to push send
              to the card's right edge is gone, and the auto margin that did the
              pushing moves here — it has to sit on whichever box is the flex
              item, and that is this wrapper whenever the tip is on. */}
          <Tooltip
            content={blocked?.hint ?? ''}
            placement="top"
            decorative
            disabled={blocked === null}
            className="prompt-box__send-slot"
          >
            <button
              type="submit"
              className="prompt-box__send"
              aria-disabled={!canSubmit}
              aria-label={
                isSubmitting
                  ? researchMode
                    ? 'Searching…'
                    : 'Generating…'
                  : (blocked?.hint ?? (researchMode ? 'Search for this paper' : 'Generate video'))
              }
            >
              <ArrowUpIcon size={18} />
            </button>
          </Tooltip>
        </div>

        {/* Outside the visible card: the picker is opened by the Add menu. */}
        <input
          ref={fileInputRef}
          type="file"
          className="prompt-box__file-input"
          accept={ACCEPTED_EXTENSIONS.join(',')}
          onChange={(event) => {
            const file = event.target.files?.[0];
            // Reset so re-picking the same file after Remove still fires change.
            event.target.value = '';
            if (file) handleFile(file);
          }}
        />
      </form>

      {readingSubject !== null && (
        <PaperPending
          subject={readingSubject}
          onCancel={cancelReading}
          returnFocusTo={textRef}
        />
      )}

      {readingSubject === null && candidates !== null && (
        <PaperCandidates
          results={candidates}
          source={candidateSource}
          query={searchedFor}
          onPick={handlePickPaper}
          onDismiss={() => {
            setCandidates(null);
            setSearchedFor('');
          }}
        />
      )}

      {creatingShelf ? (
        <div className="prompt-shelf-draft">
          <label className="prompt-shelf-draft__label" htmlFor="new-shelf-name">
            New shelf name
          </label>
          <input
            id="new-shelf-name"
            className="prompt-shelf-draft__input"
            value={newShelfName}
            autoFocus
            onChange={(event) => setNewShelfName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                void commitNewShelf();
              }
              if (event.key === 'Escape') {
                setNewShelfName('');
                setCreatingShelf(false);
              }
            }}
          />
          <Button size="sm" onClick={() => void commitNewShelf()}>
            Create
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setNewShelfName('');
              setCreatingShelf(false);
            }}
          >
            Cancel
          </Button>
        </div>
      ) : null}

      {level !== 'ok' ? (
        <p className={cx('prompt-page__balance', level === 'empty' && 'is-empty')}>
          {level === 'empty'
            ? 'You have no credits. '
            : `Credits running low (${formatCredits(balanceCredits ?? 0)} left). `}
          <Link to="/wallet">Add funds</Link>
        </p>
      ) : null}

      {banner ? (
        <div className="prompt-page__error">
          <ErrorBanner message={banner.message} onDismiss={dismissBanner} />
          {banner.walletCta ? (
            <Link className="prompt-page__wallet-link" to="/wallet">
              Go to Wallet
            </Link>
          ) : null}
        </div>
      ) : null}

      {import.meta.env.VITE_DEBUG === 'true' ? (
        <details className="prompt-advanced">
          <summary className="prompt-advanced__summary">Advanced</summary>
          <div className="prompt-advanced__grid">
            <label className="prompt-advanced__field">
              <span className="prompt-advanced__label">User ID</span>
              <input
                className="prompt-advanced__input"
                value={debugUserId}
                onChange={(e) => setDebugUserId(e.target.value)}
                placeholder="default: valoi-web"
              />
            </label>
            <label className="prompt-advanced__field">
              <span className="prompt-advanced__label">Session ID</span>
              <input
                className="prompt-advanced__input"
                value={debugSessionId}
                onChange={(e) => setDebugSessionId(e.target.value)}
                placeholder="optional"
              />
            </label>
            <label className="prompt-advanced__field">
              <span className="prompt-advanced__label">Model / provider</span>
              <input
                className="prompt-advanced__input"
                value={debugModelName}
                onChange={(e) => setDebugModelName(e.target.value)}
                placeholder="default: wan-i2v — try kling-v2-5-turbo or ltx"
              />
            </label>
            <label className="prompt-advanced__field">
              <span className="prompt-advanced__label">Duration (s per scene)</span>
              <input
                type="number"
                className="prompt-advanced__input"
                value={debugDuration}
                onChange={(e) => setDebugDuration(e.target.value ? Number(e.target.value) : '')}
                // The real default follows the model: videoGenerationApi sends
                // 6 for Wan (the default engine) and 10 for Kling/LTX. This
                // said "default: 5", which was no model's default.
                placeholder="default: 6 (wan) / 10 (kling, ltx)"
              />
            </label>
            <label className="prompt-advanced__field">
              <span className="prompt-advanced__label">Max scenes</span>
              <input
                type="number"
                className="prompt-advanced__input"
                value={debugMaxScenes}
                onChange={(e) => setDebugMaxScenes(e.target.value ? Number(e.target.value) : '')}
                placeholder="default: null"
              />
            </label>
            <label className="prompt-advanced__field">
              <span className="prompt-advanced__label">Aspect ratio</span>
              <select
                className="prompt-advanced__input"
                value={debugAspectRatio}
                onChange={(e) => setDebugAspectRatio(e.target.value)}
              >
                <option value="">Default (16:9)</option>
                <option value="16:9">16:9</option>
                <option value="9:16">9:16</option>
                <option value="1:1">1:1</option>
              </select>
            </label>
          </div>

          <label className="prompt-advanced__toggle">
            <input
              type="checkbox"
              checked={debugImageGuidance}
              onChange={(e) => setDebugImageGuidance(e.target.checked)}
            />
            <span>
              Image guidance
              <small className="prompt-advanced__hint">Previous-frame / reference-image mode</small>
            </span>
          </label>

          <Button size="sm" variant="ghost" onClick={resetDebugDefaults}>
            Reset to defaults
          </Button>
        </details>
      ) : null}

      {pendingJobId ? (
        <ConfirmGenerationModal
          open
          status={pendingJob?.status}
          estimatedCost={pendingJob?.estimatedCost}
          estimatedDurationSeconds={pendingJob?.estimatedDurationSeconds}
          errorDetail={pendingJob?.errorDetail}
          onConfirm={async () => {
            if (!pendingJob) return;
            await confirmJob(pendingJob.id);
            setPendingJobId(null);
            /* Confirming is the moment the video starts existing, so this is
               where the user is sent to watch it happen — to the video itself,
               not to the library.

               This used to go to `/library` on the grounds that the card there
               carries a thumbnail and a "Rendering" badge. It does, but a badge
               in a grid is the least the app knows: `/videos/:id` draws the
               poster full width with `JobProgressChecklist` over it — the
               four-stage breakdown, per-stage counts and a live ETA — and that
               is the screen someone who just pressed Confirm actually wants.
               Sending them to a grid to find their own card, and then click it,
               was a step backwards from the page they came from.

               `videoId ?? id`, matching Sidebar's `watchJob`: the job id and the
               library id are the same thing for a generated video, but the
               provider can hand back a distinct video id and that is the one
               the route resolves.

               After the await, not before. A confirm that fails (insufficient
               credits, most often) throws out of `transitionJob`, and the
               navigation has to be on the far side of that or the user is moved
               away from the page carrying the reason.

               The sidebar's own Review & confirm is deliberately not doing this
               — see Sidebar.tsx. That one can fire from any page, including a
               video someone is halfway through. */
            navigate(`/videos/${pendingJob.videoId ?? pendingJob.id}`);
          }}
          onCancel={async () => {
            if (!pendingJob) return;
            await cancelJob(pendingJob.id);
            setPendingJobId(null);
          }}
          onDismiss={() => setPendingJobId(null)}
        />
      ) : null}
    </div>
  );
}
